import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import type { SupportBotPresetDto, SupportKbEntryDto } from "@mvk/shared";
import { supportBotService } from "../../services/supportBotService";
import { formatApiError } from "../../api/client";
import { useAdminAuth, canManageAllEvents } from "../auth/AdminAuthContext";
import { Navigate } from "react-router";

const EMPTY_KB = { question: "", answer: "", synonyms: "", block: "Общее" };

export function AdminSupportBotGlobalPage() {
  const { user } = useAdminAuth();
  const [preset, setPreset] = useState<SupportBotPresetDto | null>(null);
  const [kb, setKb] = useState<SupportKbEntryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [persona, setPersona] = useState<"default" | "visitor_guide">("default");
  const [tab, setTab] = useState<"preset" | "kb">("preset");
  const [kbForm, setKbForm] = useState(EMPTY_KB);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await supportBotService.getGlobalPreset(persona);
      setPreset(data.preset);
      setKb(data.kb);
    } catch (err) {
      setError(formatApiError(err, "Не удалось загрузить пресет"));
    } finally {
      setLoading(false);
    }
  }, [persona]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canManageAllEvents(user)) {
    return <Navigate to="/" replace />;
  }

  const savePreset = async () => {
    if (!preset) return;
    setSaving(true);
    setError("");
    try {
      const res = await supportBotService.updateGlobalPreset({
        id: persona,
        systemPrompt: preset.systemPrompt,
        welcomeMessage: preset.welcomeMessage,
        unknownInScope: preset.unknownInScope,
        outOfScope: preset.outOfScope,
        escalatePhrase: preset.escalatePhrase,
        escalateThanks: preset.escalateThanks,
        escalateAskPhone: preset.escalateAskPhone,
        topics: preset.topics,
      });
      setPreset(res.preset);
    } catch (err) {
      setError(formatApiError(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const saveKb = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await supportBotService.updateGlobalKb(editingId, kbForm);
      } else {
        await supportBotService.createGlobalKb(kbForm);
      }
      setKbForm(EMPTY_KB);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(formatApiError(err, "Не удалось сохранить запись"));
    } finally {
      setSaving(false);
    }
  };

  const removeKb = async (id: string) => {
    if (!confirm("Удалить запись из глобальной базы?")) return;
    await supportBotService.deleteGlobalKb(id);
    await load();
  };

  if (loading || !preset) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Загрузка…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-[20px] text-gray-900">Чат-бот — глобальный пресет</h1>
        <p className="text-sm text-gray-500 mt-1">
          Одна типовая настройка на всю платформу: инструкции и общая база знаний.
          В карточке каждой выставки заполняются плейсхолдеры{" "}
          <code className="text-xs bg-gray-100 px-1 rounded">{"{{event_title}}"}</code>,{" "}
          <code className="text-xs bg-gray-100 px-1 rounded">{"{{parking}}"}</code>
          {" "}и при необходимости добавляются свои знания. Ссылки на внешние чаты —
          отдельный пункт «Ссылки поддержки».
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setPersona("default")}
          className={`px-3 py-1.5 rounded-lg text-sm ${persona === "default" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-700"}`}
        >
          Экспонент (техподдержка)
        </button>
        <button
          type="button"
          onClick={() => setPersona("visitor_guide")}
          className={`px-3 py-1.5 rounded-lg text-sm ${persona === "visitor_guide" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-700"}`}
        >
          Посетитель (справочник)
        </button>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab("preset")}
          className={`px-3 py-1.5 rounded-lg text-sm ${tab === "preset" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700"}`}
        >
          Инструкции
        </button>
        <button
          type="button"
          onClick={() => setTab("kb")}
          className={`px-3 py-1.5 rounded-lg text-sm ${tab === "kb" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700"}`}
        >
          База знаний ({kb.length})
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {tab === "preset" && (
        <div className="space-y-3 bg-white border border-gray-200 rounded-xl p-4">
          {(
            [
              ["welcomeMessage", "Приветствие", 3],
              ["systemPrompt", "System prompt / инструкции", 12],
              ["unknownInScope", "Нет ответа в базе (по теме)", 2],
              ["outOfScope", "Вне темы", 2],
              ["escalatePhrase", "Эскалация", 2],
              ["escalateThanks", "После передачи менеджеру", 2],
              ["escalateAskPhone", "Запрос телефона", 2],
            ] as const
          ).map(([key, label, rows]) => (
            <label key={key} className="block">
              <span className="text-xs font-medium text-gray-600">{label}</span>
              <textarea
                rows={rows}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={preset[key]}
                onChange={(e) => setPreset({ ...preset, [key]: e.target.value })}
              />
            </label>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Темы (чипы), через запятую</span>
            <input
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={preset.topics.join(", ")}
              onChange={(e) =>
                setPreset({
                  ...preset,
                  topics: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
          <button
            type="button"
            disabled={saving}
            onClick={() => void savePreset()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Сохранить пресет
          </button>
        </div>
      )}

      {tab === "kb" && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <div className="text-sm font-medium text-gray-800">
              {editingId ? "Редактировать запись" : "Новая запись"}
            </div>
            <input
              placeholder="Вопрос"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={kbForm.question}
              onChange={(e) => setKbForm({ ...kbForm, question: e.target.value })}
            />
            <textarea
              placeholder="Ответ (можно {{manager_name}}, {{parking}}, …)"
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={kbForm.answer}
              onChange={(e) => setKbForm({ ...kbForm, answer: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                placeholder="Синонимы"
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={kbForm.synonyms}
                onChange={(e) => setKbForm({ ...kbForm, synonyms: e.target.value })}
              />
              <input
                placeholder="Блок"
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={kbForm.block}
                onChange={(e) => setKbForm({ ...kbForm, block: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving || !kbForm.question || !kbForm.answer}
                onClick={() => void saveKb()}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
              >
                <Plus size={14} /> {editingId ? "Обновить" : "Добавить"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-gray-100 text-sm"
                  onClick={() => {
                    setEditingId(null);
                    setKbForm(EMPTY_KB);
                  }}
                >
                  Отмена
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {kb.map((e) => (
              <div
                key={e.id}
                className="bg-white border border-gray-200 rounded-xl p-3 text-sm"
              >
                <div className="font-medium text-gray-900">{e.question}</div>
                <div className="text-xs text-gray-400 mt-0.5">{e.block}</div>
                <div className="text-gray-600 mt-1 line-clamp-3 whitespace-pre-wrap">{e.answer}</div>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="text-indigo-600 text-xs"
                    onClick={() => {
                      setEditingId(e.id);
                      setKbForm({
                        question: e.question,
                        answer: e.answer,
                        synonyms: e.synonyms,
                        block: e.block,
                      });
                    }}
                  >
                    Изменить
                  </button>
                  <button
                    type="button"
                    className="text-red-600 text-xs inline-flex items-center gap-1"
                    onClick={() => void removeKb(e.id)}
                  >
                    <Trash2 size={12} /> Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
